/**
 * Finance Overview.
 *
 * The waterfall, working capital, the cash forecast summary, the ledgers card, the month
 * close checklist and the product P and L top table, in the arrangement the design settled
 * on. Every number arrives inside a metric envelope and renders through the shared
 * components, so no figure on this page was computed in the browser.
 *
 * Three things this page will not do:
 *   render a zero for a number it could not get,
 *   put the accrual waterfall and the cash forecast in one table,
 *   use the word "margin" without saying which of the three it means.
 */
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import type { MetricEnvelope, WindowKind } from '@elutide/shared';
import {
  BasisBadge,
  Card,
  Chip,
  DataTable,
  EmptyState,
  ErrorState,
  MoneyText,
  Pill,
  Skeleton,
  type Column,
} from '@/components';
import { formatMoney, formatPercent } from '@/lib/format';
import { useFinanceOverview, useForecast, useMonthClose, useProductPnl } from './api';
import { WaterfallChart } from './charts';
import type { CloseStep, ProductPnlRow, WorkingCapital } from './types';
import './finance.css';

const WINDOWS: readonly { id: WindowKind; label: string }[] = [
  { id: '7d', label: '7d' },
  { id: '14d', label: '14d' },
  { id: '30d', label: '30d' },
  { id: '90d', label: '90d' },
  { id: 'mtd', label: 'MTD' },
];

function currentPeriod(): string {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** A window picker that changes the whole page, not one card. */
export function WindowChips({ value, onChange }: { value: WindowKind; onChange: (next: WindowKind) => void }) {
  return (
    <div className="o-chiprow" data-allow-hscroll="true">
      {WINDOWS.map((w) => (
        <Chip key={w.id} label={w.label} active={value === w.id} onClick={() => onChange(w.id)} />
      ))}
    </div>
  );
}

export function FinanceOverview() {
  const [window, setWindow] = useState<WindowKind>('30d');
  const overview = useFinanceOverview(window);
  const products = useProductPnl(window, 8);
  const forecast = useForecast(90, 'base');
  const close = useMonthClose(currentPeriod());

  if (overview.isLoading) {
    return (
      <div className="o-stack">
        <Skeleton shape="card" />
        <Skeleton shape="card" />
      </div>
    );
  }

  if (overview.isError || !overview.data) {
    return (
      <ErrorState
        message="Finance could not be loaded."
        onRetry={() => {
          void overview.refetch();
        }}
      />
    );
  }

  const data = overview.data;
  const coverage = data.fee_coverage;
  const missing = data.missing;

  return (
    <div className="o-stack">
      <WindowChips value={window} onChange={setWindow} />

      <div className="o-split">
        <Card
          title={`True net · ${data.net.window.label}`}
          basis="woo_paid"
          window={data.net.window}
          partial={data.net.partial}
          asOf={data.net.as_of}
          ageClass="snapshot"
          action={
            <span className="o-chart__legend--markers">
              <BasisBadge
                basis="woo_paid"
                window={data.net.window}
                also={[
                  {
                    basis: 'estimate',
                    note:
                      coverage.actual_share === null
                        ? 'fees'
                        : `fees ${formatPercent(1 - coverage.actual_share)} EST`,
                  },
                ]}
              />
            </span>
          }
        >
          <WaterfallChart bars={data.waterfall} />

          <p className="o-margins">
            <span>
              Gross margin <b>{formatPercent(data.margins.gross_margin_pct.value)}</b>
            </span>
            <span>
              Contribution margin <b>{formatPercent(data.margins.contribution_margin_pct.value)}</b>
            </span>
            <span>
              Net margin <b>{formatPercent(data.margins.net_margin_pct.value)}</b>
            </span>
            {data.context.map((envelope) => (
              <span key={envelope.metric_id}>
                {envelope.metric_id.replace(/_/g, ' ')}{' '}
                <b>{envelope.value === null ? 'no data' : envelope.unit === 'cents' ? formatMoney(envelope.value) : String(envelope.value)}</b>
              </span>
            ))}
          </p>

          {missing.length > 0 ? (
            <p className="o-muted">
              Incomplete: {missing.join(', ')} could not be computed for this window, so the net
              figure is short by whatever they hold. It is not zero.
            </p>
          ) : null}

          {!data.reconciles ? (
            <p className="o-card__error" role="status">
              The composed lines and the registry true_net disagree. Both figures are shown so the
              difference is visible rather than resolved silently.
            </p>
          ) : null}
        </Card>

        <WorkingCapitalCard capital={data.cash} bank={data.bank_balance} qbo={data.qbo} />
      </div>

      <div className="o-split">
        <Card
          title="Cash forecast · 90 days"
          basis="model"
          asOf={forecast.data?.run_date ?? null}
          ageClass="model"
          action={<Link to="/finance/forecast">Assumptions</Link>}
        >
          {forecast.isLoading ? (
            <Skeleton shape="card" />
          ) : forecast.data ? (
            <>
              <div className="o-scorecard">
                <div className="o-kpi">
                  <span className="o-kpi__label">Day 30</span>
                  <span className="o-kpi__value">{formatMoney(forecast.data.closing.d30, { showCents: false })}</span>
                  <span className="o-kpi__meta">
                    low point {formatMoney(forecast.data.low_point.cents, { showCents: false })} on{' '}
                    {forecast.data.low_point.date ?? 'no date'}
                  </span>
                </div>
                <div className="o-kpi">
                  <span className="o-kpi__label">Day 60</span>
                  <span className="o-kpi__value">{formatMoney(forecast.data.closing.d60, { showCents: false })}</span>
                  <span className="o-kpi__meta">
                    {forecast.data.first_breach_date
                      ? `below the floor from ${forecast.data.first_breach_date}`
                      : 'stays above the buffer floor'}
                  </span>
                </div>
                <div className="o-kpi">
                  <span className="o-kpi__label">Day 90</span>
                  <span className="o-kpi__value">{formatMoney(forecast.data.closing.d90, { showCents: false })}</span>
                  <span className="o-kpi__meta">8 week weekday profile</span>
                </div>
              </div>
              <p className="o-muted">
                Cash, not profit. Inputs: trailing paid revenue by weekday, payout lag by method,
                purchase order due dates, tax due dates, the fixed cost schedule, held fund release
                dates and the ad budget. Every one of them is AUTO or OVERRIDE and listed on the
                Forecast page.
              </p>
            </>
          ) : (
            <EmptyState message="The forecast has not been run for this scenario yet." />
          )}
        </Card>

        <div className="o-stack">
          <LedgersCard
            monthlyNutCents={data.fixed_costs.monthly_nut_cents}
            lineCount={data.fixed_costs.line_count}
            capital={data.cash}
          />
          <MonthCloseCard period={currentPeriod()} steps={close.data?.steps ?? []} reviewed={close.data?.reviewed ?? false} />
        </div>
      </div>

      <Card
        title={`Product P and L · ${data.net.window.label}`}
        basis="woo_paid"
        window={data.net.window}
        asOf={data.net.as_of}
        ageClass="snapshot"
        action={<Link to="/finance/pnl">Full P and L</Link>}
      >
        <ProductPnlTable rows={products.data?.items ?? []} loading={products.isLoading} />
      </Card>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Working capital                                                     */
/* ------------------------------------------------------------------ */

const CAPITAL_LABELS: Record<string, string> = {
  inventory_at_cost: 'Inventory at cost (stock times WAC)',
  pending_inbound_value: 'Inbound at landed cost',
  held_funds_balance: 'Held funds (processor reserve)',
  gateway_receivable: 'Gateway receivable',
  po_balance_due: 'Less: purchase order commitment owed',
  rr_liability: 'Less: Research Rewards liability',
  inventory_at_retail: 'Memo: inventory at retail',
};

export function WorkingCapitalCard({
  capital,
  bank,
  qbo,
}: {
  capital: WorkingCapital;
  bank: { cents: number | null; as_of: string | null; source: string };
  qbo: { connected: boolean; last_ok_at: string | null };
}) {
  const row = (envelope: MetricEnvelope, owed: boolean) => (
    <div key={envelope.metric_id} className={`o-kv${owed ? ' o-kv--owed' : ''}`}>
      <span className="o-kv__label">{CAPITAL_LABELS[envelope.metric_id] ?? envelope.metric_id}</span>
      <span className="o-kv__value">
        {envelope.value === null ? (
          <span className="o-muted">no data</span>
        ) : (
          <MoneyText cents={owed ? -Math.abs(envelope.value) : envelope.value} showCents={false} />
        )}
      </span>
    </div>
  );

  return (
    <Card title="Working capital" basis="ledger" asOf={capital.as_of} ageClass="snapshot">
      {capital.owned.map((e) => row(e, false))}
      {capital.owed.map((e) => row(e, true))}
      <div className="o-kv o-kv--total">
        <span className="o-kv__label">Net working capital</span>
        <span className="o-kv__value">
          {capital.net_cents === null ? (
            <span className="o-muted">incomplete</span>
          ) : (
            <MoneyText cents={capital.net_cents} showCents={false} />
          )}
        </span>
      </div>
      {capital.memo.map((e) => (
        <div key={e.metric_id} className="o-kv">
          <span className="o-kv__label">{CAPITAL_LABELS[e.metric_id] ?? e.metric_id}</span>
          <span className="o-kv__value o-muted">
            {e.value === null ? 'no data' : formatMoney(e.value, { showCents: false })}
          </span>
        </div>
      ))}
      <div className="o-kv">
        <span className="o-kv__label">Bank balance</span>
        <span className="o-kv__value">
          {bank.cents === null ? (
            <span className="o-muted">connect QuickBooks or enter it in Settings</span>
          ) : (
            <>
              <MoneyText cents={bank.cents} showCents={false} />{' '}
              <Pill tone={qbo.connected ? 'info' : 'neutral'}>{bank.source}</Pill>
            </>
          )}
        </span>
      </div>
      <p className="o-muted">
        At cost, point in time. Retail values and any realised factor are not assets and are not in
        this total. Goods not yet owned sit in the memo line with their payable, not in the total.
      </p>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Ledgers summary                                                     */
/* ------------------------------------------------------------------ */

export function LedgersCard({
  monthlyNutCents,
  lineCount,
  capital,
}: {
  monthlyNutCents: number;
  lineCount: number;
  capital: WorkingCapital;
}) {
  const held = capital.owned.find((e) => e.metric_id === 'held_funds_balance');
  const rr = capital.owed.find((e) => e.metric_id === 'rr_liability');

  return (
    <Card title="Ledgers" basis="ledger" asOf={capital.as_of} ageClass="snapshot" action={<Link to="/finance/ledgers">Open</Link>}>
      <div className="o-kv">
        <span className="o-kv__label">Fixed costs, monthly nut</span>
        <span className="o-kv__value">
          <MoneyText cents={monthlyNutCents} showCents={false} /> <span className="o-muted">{lineCount} active</span>
        </span>
      </div>
      <div className="o-kv">
        <span className="o-kv__label">Held funds</span>
        <span className="o-kv__value">
          {held?.value === null || held === undefined ? (
            <span className="o-muted">no data</span>
          ) : (
            <MoneyText cents={held.value} showCents={false} />
          )}
        </span>
      </div>
      <div className="o-kv">
        <span className="o-kv__label">Research Rewards liability</span>
        <span className="o-kv__value">
          {rr?.value === null || rr === undefined ? (
            <span className="o-muted">no data</span>
          ) : (
            <MoneyText cents={rr.value} showCents={false} />
          )}
        </span>
      </div>
      <p className="o-muted">
        Append only. Every money change carries a reason, and the application database user has no
        UPDATE or DELETE on the audit log.
      </p>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Month close                                                         */
/* ------------------------------------------------------------------ */

const STEP_MARK: Record<CloseStep['state'], string> = {
  passed: 'ok',
  passed_with_exceptions: '!',
  failed: 'x',
  open: '-',
  waived: '~',
};

export function MonthCloseCard({ period, steps, reviewed }: { period: string; steps: CloseStep[]; reviewed: boolean }) {
  const done = steps.filter((s) => s.state === 'passed' || s.state === 'waived').length;
  return (
    <Card
      title={`Month close · ${period}`}
      basis="ledger"
      action={<Pill tone={reviewed ? 'ok' : done === steps.length ? 'ok' : 'warn'}>{`${done} of ${steps.length}`}</Pill>}
    >
      {steps.length === 0 ? (
        <EmptyState message="Nothing to close for this period yet." />
      ) : (
        <ul className="o-checklist">
          {steps.map((step) => (
            <li key={step.step} className="o-checklist__item">
              <span className={`o-checklist__mark o-checklist__mark--${step.state}`} aria-hidden="true">
                {STEP_MARK[step.state]}
              </span>
              <span>
                {step.label}
                <span className="o-checklist__detail"> {describeStep(step)}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className="o-muted">
        A checklist and a reviewed flag. There is no period lock at launch, so nothing here freezes
        a figure and a later correction flows through normally.
      </p>
    </Card>
  );
}

function describeStep(step: CloseStep): string {
  const detail = step.detail;
  switch (step.step) {
    case 'payout_import': {
      const payouts = Number(detail['payouts'] ?? 0);
      const unmatched = Number(detail['unmatched_lines'] ?? 0);
      if (payouts === 0) return 'no statement imported yet';
      return `${payouts} imported, ${unmatched} unmatched lines`;
    }
    case 'fees_actual_share': {
      const share = detail['actual_share'];
      if (share === null || share === undefined) return 'no fees in this period';
      return `${formatPercent(Number(share))} actual, ${Number(detail['stale_estimate_orders'] ?? 0)} stale estimates`;
    }
    case 'reserve_booked':
      return Number(detail['rows'] ?? 0) === 0
        ? 'nothing booked'
        : `${Number(detail['rows'])} rows, ${formatMoney(Number(detail['booked_cents'] ?? 0), { showCents: false })}`;
    case 'review_comps_refunds':
      return `${Number(detail['comps'] ?? 0)} comps, ${Number(detail['refunds'] ?? 0)} refunds, ${Number(
        detail['lines_without_cost'] ?? 0,
      )} lines without a cost`;
    default:
      return step.note ?? '';
  }
}

/* ------------------------------------------------------------------ */
/* Product P and L                                                     */
/* ------------------------------------------------------------------ */

export function ProductPnlTable({ rows, loading }: { rows: ProductPnlRow[]; loading: boolean }) {
  const columns: readonly Column<ProductPnlRow>[] = [
    { id: 'sku', header: 'SKU', priority: 1, mono: true, render: (r) => r.sku ?? r.name },
    { id: 'units', header: 'Units', priority: 2, align: 'right', render: (r) => r.units },
    {
      id: 'revenue',
      header: 'Revenue',
      priority: 1,
      align: 'right',
      money: true,
      render: (r) => <MoneyText cents={r.revenue_cents} showCents={false} />,
    },
    {
      id: 'cogs',
      header: 'COGS (WAC)',
      priority: 3,
      align: 'right',
      money: true,
      render: (r) => <MoneyText cents={r.cogs_cents} showCents={false} />,
    },
    {
      id: 'fees',
      header: 'Fees',
      priority: 4,
      align: 'right',
      money: true,
      render: (r) => <MoneyText cents={r.processor_fee_cents} showCents={false} />,
    },
    {
      id: 'gross_profit',
      header: 'Gross profit',
      priority: 1,
      align: 'right',
      money: true,
      render: (r) => <MoneyText cents={r.gross_profit_cents} showCents={false} />,
    },
    {
      id: 'gross_margin',
      header: 'Gross margin',
      priority: 2,
      align: 'right',
      money: true,
      render: (r) =>
        r.gross_margin_pct === null ? (
          <span className="o-muted" title="This SKU sold without a cost, so a margin would be a fiction.">
            n/a
          </span>
        ) : (
          formatPercent(r.gross_margin_pct)
        ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={rows}
      rowKey={(r) => String(r.product_id)}
      caption="Product profit and loss for the selected window"
      loading={loading}
      emptyState={<EmptyState message="No paid lines in this window." />}
    />
  );
}
